import React from 'react'
import { Stack, Autocomplete, MultiSelect, Group, Text, Accordion, Badge, Select } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { DriveBillingType, DriveType, RouteResponse, VehicleResponse } from '@/api/main/types.gen'
import dayjs from 'dayjs'
import { TbSettings } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

export interface DriveFormValues {
  route_id: string
  route_version: string
  drive_type: Array<DriveType>
  vehicle_id?: string
  start_after: string
  finish_before: string
  billing_type: DriveBillingType | '' // Changed from Array<DriveBillingType>
}

const DRIVE_TYPES: DriveType[] = ['statistical', 'evidence', 'documentation', 'testing', 'special']

const BILLING_TYPES: DriveBillingType[] = ['billable', 'non_billable', 'development', 'disrespect_all']

export function DriveFormModal({
  modalKey,
  routes,
  vehicles,
  initialValues,
  onSubmit,
  children,
}: {
  modalKey: string
  routes: RouteResponse[]
  vehicles?: VehicleResponse[]
  initialValues?: Partial<DriveFormValues>
  onSubmit: (values: DriveFormValues) => void
  isLoading?: boolean
  children?: React.ReactNode
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const form = useForm<DriveFormValues>({
    initialValues: {
      route_id: initialValues?.route_id || '',
      route_version: initialValues?.route_version || '',
      drive_type: initialValues?.drive_type || [],
      vehicle_id: initialValues?.vehicle_id || '',
      start_after: initialValues?.start_after || '',
      finish_before: initialValues?.finish_before || '',
      billing_type: initialValues?.billing_type || '', // Changed from []
    },
    validate: {
      route_id: value => (!value ? t('routeRequired') : null),
      route_version: value => (!value ? t('routeVersionRequired') : null),
      drive_type: value => (value.length === 0 ? t('atLeastOneDriveTypeRequired') : null),
      vehicle_id: value => (vehicles && !value ? t('vehicleRequired') : null),
      start_after: (value, values) => {
        if (!value) return t('startAfterRequired')
        if (values.finish_before && dayjs(value).isAfter(dayjs(values.finish_before)))
          return t('startAfterBeforeFinishBefore')
        return null
      },
      finish_before: (value, values) => {
        if (!value) return t('finishBeforeRequired')
        if (values.start_after && dayjs(value).isBefore(dayjs(values.start_after)))
          return t('finishBeforeAfterStartAfter')
        return null
      },
      billing_type: value => (!value ? t('billingTypeRequired') : null),
    },
  })

  // Get unique route IDs for autocomplete
  const routeOptions = React.useMemo(() => {
    const uniqueRoutes = new Map<string, RouteResponse>()
    routes.forEach(route => {
      if (!uniqueRoutes.has(route.id)) {
        uniqueRoutes.set(route.id, route)
      }
    })
    return Array.from(uniqueRoutes.values()).map(route => ({
      value: route.id,
      label: route.name ? `${route.name} | ${route.id}` : route.id,
    }))
  }, [routes])

  // Get vehicle options for autocomplete
  const vehicleOptions = React.useMemo(() => {
    if (!vehicles) return []
    return vehicles.map(vehicle => ({
      value: vehicle.id,
      label: vehicle.id,
    }))
  }, [vehicles])

  React.useEffect(() => {
    if (form.values.route_id) {
      const selectedRoute = routes.find(r => r.id === form.values.route_id)
      if (selectedRoute) {
        form.setFieldValue('route_version', selectedRoute.version)
        return
      }
    }

    form.setFieldValue('route_version', '')
    // form is a stable Mantine form ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.route_id, routes])

  React.useEffect(() => {
    form.setValues({
      route_id: initialValues?.route_id || '',
      route_version: initialValues?.route_version || '',
      drive_type: initialValues?.drive_type || [],
      vehicle_id: initialValues?.vehicle_id || '',
      start_after: initialValues?.start_after || '',
      finish_before: initialValues?.finish_before || '',
      billing_type: initialValues?.billing_type || '',
    })
    form.resetDirty()
    // form is a stable Mantine form ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modalKey,
    initialValues?.route_id,
    initialValues?.route_version,
    initialValues?.drive_type,
    initialValues?.vehicle_id,
    initialValues?.start_after,
    initialValues?.finish_before,
    initialValues?.billing_type,
  ])

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  return (
    <form onSubmit={handleSubmit} id="drive-form" key={modalKey}>
      <Stack gap="sm">
        {vehicles && (
          <Autocomplete
            label={t('vehicle')}
            placeholder={t('selectVehicle')}
            data={vehicleOptions}
            {...form.getInputProps('vehicle_id')}
          />
        )}

        <Select
          searchable
          clearable
          label={
            <Text fz={14} fw={500}>
              {t('version')}:{' '}
              <strong>{form.getInputProps('route_version').value || t('notAvailable', { ns: 'common' })}</strong>
            </Text>
          }
          placeholder={t('selectRoute')}
          data={routeOptions}
          {...form.getInputProps('route_id')}
        />

        <DateTimePicker
          label={t('startAfter')}
          placeholder={t('selectStartTime')}
          value={form.values.start_after ? dayjs(form.values.start_after).toDate() : null}
          onChange={date => {
            form.setFieldValue('start_after', date ? dayjs(date).toISOString() : '')
          }}
          error={form.getInputProps('start_after').error}
        />

        <DateTimePicker
          label={t('finishBefore')}
          placeholder={t('selectFinishTime')}
          value={form.values.finish_before ? dayjs(form.values.finish_before).toDate() : null}
          onChange={date => {
            form.setFieldValue('finish_before', date ? dayjs(date).toISOString() : '')
            form.validateField('start_after')
          }}
          error={form.getInputProps('finish_before').error}
        />
      </Stack>

      <Accordion
        radius="md"
        mb="md"
        mt="md"
        styles={{
          control: {
            paddingInline: 'unset',
          },
          item: {
            border: 'none',
          },
        }}
      >
        <Accordion.Item value={'advanced'}>
          <Accordion.Control icon={<TbSettings size={20} color="var(--mantine-color-dimmed)" />}>
            {t('advancedSettings')}
          </Accordion.Control>
          <Accordion.Panel
            mb="md"
            styles={{
              content: {
                padding: '0',
              },
            }}
          >
            <MultiSelect
              mt="sm"
              mb="md"
              label={t('driveType')}
              placeholder={t('selectDriveTypes')}
              data={DRIVE_TYPES.filter(type => !form.values.drive_type.includes(type)).map(type => ({
                value: type,
                label: type,
              }))}
              {...form.getInputProps('drive_type')}
              renderOption={({ option }) => (
                <Group gap="xs">
                  <Badge variant="light" color="blue">
                    {option.label}
                  </Badge>
                </Group>
              )}
            />
          </Accordion.Panel>
          <Accordion.Panel
            mb="md"
            styles={{
              content: {
                padding: '0',
              },
            }}
          >
            <Select
              mt="sm"
              mb="md"
              label={t('billingType')}
              placeholder={t('selectBillingType')}
              data={BILLING_TYPES.map(type => ({
                value: type,
                label: type,
              }))}
              {...form.getInputProps('billing_type')}
              renderOption={({ option }) => (
                <Group gap="xs">
                  <Badge variant="light" color="blue">
                    {option.value}
                  </Badge>
                </Group>
              )}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {children}
    </form>
  )
}
